import {
  TConfirmPasswordResetPayload,
  TConfirmPasswordResetResponse,
  TCreateSessionPayload,
  TUserResponseType
} from 'typings/userTypes';
import { homeSecurityPortalApi } from 'data/axios';
import { ApiResponse } from './HttpRequest';
import { ATLAS_ENDPOINTS } from './endpoints';

// The session is established via an HttpOnly cookie set by the backend;
// the response body carries nothing the client needs. skipSessionGate: this IS
// the request that creates the session — gating it would deadlock, and its 401
// must reach AppBridge's retry instead of closing the webview.
export async function CreateSession(
  payload: TCreateSessionPayload
): Promise<void> {
  await homeSecurityPortalApi.post(ATLAS_ENDPOINTS.CREATE_SESSION, payload, {
    skipSessionGate: true
  });
}

export async function LogoutSession(): Promise<void> {
  try {
    await homeSecurityPortalApi.post(ATLAS_ENDPOINTS.LOGOUT_SESSION);
  } catch (error) {
    console.error('Session logout error:', error);
  }
}

export async function GetUserDetails(): Promise<
  ApiResponse<TUserResponseType>
> {
  try {
    const response = await homeSecurityPortalApi.get(ATLAS_ENDPOINTS.USER);
    console.log('API Response:', response);
    return response;
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw error;
  }
}

// skipSessionGate on both: the devMockBridge token mint runs these while the
// webview session gate is armed — gating them would deadlock the mock boot.
export async function GetOneTimeToken() {
  const response = await homeSecurityPortalApi.get(
    ATLAS_ENDPOINTS.GENERATE_ONE_TIME_TOKEN,
    { skipSessionGate: true }
  );
  return response;
}

export async function VerifyOneTimeToken(oneTimeToken: string) {
  try {
    const response = await homeSecurityPortalApi.post(
      ATLAS_ENDPOINTS.VERIFY_ONE_TIME_TOKEN,
      {
        one_time_token: oneTimeToken
      },
      { skipSessionGate: true }
    );
    return response;
  } catch (error) {
    console.error('Error verifying one-time token:', error);
    throw error;
  }
}

export async function DeleteUserAccount() {
  try {
    const response = await homeSecurityPortalApi.delete(ATLAS_ENDPOINTS.USER);
    return response;
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw error; // Ensure the error is propagated correctly
  }
}

// ConfirmPasswordReset applies a new password using the token from the reset email.
// Unauthenticated by design — the user cannot sign in, so the token is the only
// capability. The error is rethrown so the page can map the backend's `code` to a
// message rather than showing one generic failure for every cause.
export async function ConfirmPasswordReset(
  payload: TConfirmPasswordResetPayload
): Promise<ApiResponse<TConfirmPasswordResetResponse>> {
  try {
    const response = await homeSecurityPortalApi.post(
      ATLAS_ENDPOINTS.CONFIRM_PASSWORD_RESET,
      payload
    );
    return response;
  } catch (error) {
    // Deliberately not logging the payload — it carries the new password.
    console.error('Error confirming password reset');
    throw error;
  }
}

export async function InitiateDeleteUserAccount(email: string) {
  try {
    const response = await homeSecurityPortalApi.post(
      ATLAS_ENDPOINTS.REQUEST_ACCOUNT_DELETE,
      { email }
    );
    return response;
  } catch (error) {
    console.error('Error initiating account delete:', error);
    throw error;
  }
}
